import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import Layout from '../components/layout/Layout';
import { useAtm } from '../context/AtmContext';
import { AtmOperationType, AtmOption, AtmTheme, CreateAtmDto } from '../types/atm';
import { trackAtmCreation } from '../utils/trackingUtils';

/**
 * Get theme-specific styling classes
 */
const getThemeStyles = (theme: AtmTheme): string => {
  switch (theme) {
    case AtmTheme.DEFAULT:
      return 'bg-blue-500 text-white border-blue-600';
    case AtmTheme.CLASSIC:
      return 'bg-green-600 text-white border-green-700';
    case AtmTheme.MODERN:
      return 'bg-gradient-to-r from-purple-500 to-blue-600 text-white border-purple-400';
    case AtmTheme.RETRO:
      return 'bg-orange-400 text-black border-orange-500';
    case AtmTheme.DARK:
      return 'bg-gray-900 text-green-400 border-gray-700';
    default:
      return 'bg-blue-500 text-white border-blue-600';
  }
};

/**
 * Get theme description
 */
const getThemeDescription = (theme: AtmTheme): string => {
  switch (theme) {
    case AtmTheme.DEFAULT:
      return 'Clean and professional';
    case AtmTheme.CLASSIC:
      return 'Traditional bank styling';
    case AtmTheme.MODERN:
      return 'Sleek gradient design';
    case AtmTheme.RETRO:
      return 'Vintage 80s aesthetic';
    case AtmTheme.DARK:
      return 'Dark mode with green text';
    default:
      return 'Standard theme';
  }
};

/**
 * Page for creating a new ATM
 */
const CreateAtm: React.FC = () => {
  const navigate = useNavigate();
  const { createNewAtm, error, clearError } = useAtm();

  // State for ATM data
  const [atmName, setAtmName] = useState<string>('');
  const [atmDescription, setAtmDescription] = useState<string>('');
  const [withdrawalLimit, setWithdrawalLimit] = useState<number>(100);
  const [requirePin, setRequirePin] = useState<boolean>(true);
  const [defaultPin, setDefaultPin] = useState<string>('1234');
  const [selectedTheme, setSelectedTheme] = useState<AtmTheme>(AtmTheme.DEFAULT);

  // Default withdrawal amounts - simplified approach
  const [withdrawalAmounts, setWithdrawalAmounts] = useState<number[]>([5, 10, 20, 30, 50]);
  const [showEditAmounts, setShowEditAmounts] = useState<boolean>(false);

  // UI state
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  /**
   * Handle editing custom withdrawal amounts
   */
  const handleEditAmounts = () => {
    setShowEditAmounts(!showEditAmounts);
  };

  /**
   * Update a specific withdrawal amount
   */
  const updateWithdrawalAmount = (index: number, newAmount: number) => {
    const newAmounts = [...withdrawalAmounts];
    newAmounts[index] = newAmount;
    setWithdrawalAmounts(newAmounts);
  };

  /**
   * Delete a withdrawal amount
   */
  const deleteWithdrawalAmount = (index: number) => {
    const newAmounts = withdrawalAmounts.filter((_, i) => i !== index);
    setWithdrawalAmounts(newAmounts);
  };

  /**
   * Add a new withdrawal amount
   */
  const addWithdrawalAmount = () => {
    const newAmounts = [...withdrawalAmounts, 25]; // Default to $25
    setWithdrawalAmounts(newAmounts);
  };

  /**
   * Validate ATM data
   */
  const validateAtm = (): boolean => {
    if (!atmName.trim()) {
      setValidationError('Please enter a name for your ATM');
      return false;
    }

    if (withdrawalLimit <= 0) {
      setValidationError('Withdrawal limit must be greater than $0');
      return false;
    }

    if (requirePin && (!defaultPin || defaultPin.length < 4)) {
      setValidationError('PIN must be at least 4 characters');
      return false;
    }

    setValidationError(null);
    return true;
  };

  /**
   * Handle saving the ATM
   */
  const handleSaveAtm = async () => {
    if (!validateAtm()) return;

    try {
      setIsSaving(true);
      clearError();

      const options: AtmOption[] = [];

      // Add withdrawal options from our simplified approach
      withdrawalAmounts.forEach((amount, index) => {
        if (amount <= withdrawalLimit) {
          // Only include amounts within the limit
          options.push({
            id: uuidv4(),
            text: `Withdraw $${amount}`,
            type: AtmOperationType.WITHDRAWAL,
            amount: amount,
            enabled: true,
            position: index,
          });
        }
      });

      const atmData: CreateAtmDto = {
        name: atmName.trim(),
        description: atmDescription.trim(),
        theme: selectedTheme,
        isPublic: false,
        options,
        atmSettings: {
          requirePin,
          defaultPin: requirePin ? defaultPin : undefined,
          animationSpeed: 1,
          showInExplore: false,
          sounds: {
            enabled: true,
          },
        },
      };

      const atmId = await createNewAtm(atmData);

      // Track creation
      trackAtmCreation(atmId, atmName, {
        theme: selectedTheme,
        isPublic: false,
        optionCount: options.length, // Multiple withdrawal options + balance + cancel
        requiresPin: requirePin,
      });

      // Navigate to the new ATM
      navigate(`/atm/${atmId}`);
    } catch (err) {
      console.error('Error creating ATM:', err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Create New ATM</h1>
          <p className="text-gray-600">
            Set up your ATM with custom withdrawal amounts and transaction limits. Check Balance and
            Cancel options are included by default.
          </p>
        </div>

        {/* Error Display */}
        {(error || validationError) && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-700">{error || validationError}</p>
          </div>
        )}

        {/* Form */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-8">
          <div className="space-y-6">
            {/* ATM Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="atmName">
                ATM Name *
              </label>
              <input
                id="atmName"
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter ATM name"
                value={atmName}
                onChange={e => setAtmName(e.target.value)}
                maxLength={50}
              />
              <p className="text-xs text-gray-500 mt-1">{atmName.length}/50 characters</p>
            </div>

            {/* Description */}
            <div>
              <label
                className="block text-sm font-medium text-gray-700 mb-2"
                htmlFor="atmDescription"
              >
                Description (Optional)
              </label>
              <textarea
                id="atmDescription"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Describe your ATM"
                value={atmDescription}
                onChange={e => setAtmDescription(e.target.value)}
                rows={3}
                maxLength={200}
              />
              <p className="text-xs text-gray-500 mt-1">{atmDescription.length}/200 characters</p>
            </div>

            {/* Theme Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">ATM Theme *</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.values(AtmTheme).map(theme => (
                  <div
                    key={theme}
                    onClick={() => setSelectedTheme(theme)}
                    className={`relative cursor-pointer rounded-lg border-2 transition-all ${
                      selectedTheme === theme
                        ? 'border-blue-500 ring-2 ring-blue-200'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {/* Theme Preview */}
                    <div className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-t-lg">
                      <div
                        className={`w-full h-24 rounded-md flex items-center justify-center text-sm font-medium ${getThemeStyles(
                          theme
                        )}`}
                      >
                        <div className="text-center">
                          <div className="w-8 h-8 mx-auto mb-2 rounded border-2 border-current opacity-75" />
                          <div>ATM {theme.charAt(0).toUpperCase() + theme.slice(1)}</div>
                        </div>
                      </div>
                    </div>

                    {/* Theme Name */}
                    <div className="p-3 text-center">
                      <h3 className="text-sm font-medium text-gray-900 capitalize">
                        {theme === AtmTheme.DEFAULT ? 'Default' : theme}
                      </h3>
                      <p className="text-xs text-gray-500 mt-1">{getThemeDescription(theme)}</p>
                    </div>

                    {/* Selected indicator */}
                    {selectedTheme === theme && (
                      <div className="absolute top-2 right-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                        <svg
                          className="w-4 h-4 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Withdrawal Limit */}
            <div>
              <label
                className="block text-sm font-medium text-gray-700 mb-2"
                htmlFor="withdrawalLimit"
              >
                Withdrawal Limit *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                  $
                </span>
                <input
                  id="withdrawalLimit"
                  type="number"
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="100"
                  value={withdrawalLimit}
                  onChange={e => setWithdrawalLimit(Number(e.target.value))}
                  min="1"
                  step="1"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                The amount users can withdraw in a single transaction
              </p>
            </div>

            {/* Withdrawal Amounts */}
            <div className="border-t pt-6">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  Withdrawal Options *
                </label>
                <button
                  type="button"
                  onClick={handleEditAmounts}
                  className="px-3 py-1 text-sm text-blue-600 border border-blue-600 rounded hover:bg-blue-50 transition-colors"
                >
                  {showEditAmounts ? 'Done' : 'Edit Amounts'}
                </button>
              </div>

              {showEditAmounts ? (
                <div className="space-y-3">
                  {withdrawalAmounts.map((amount, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600 w-4">$</span>
                      <input
                        type="number"
                        value={amount}
                        onChange={e => updateWithdrawalAmount(index, Number(e.target.value))}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        min="1"
                        max={withdrawalLimit}
                      />
                      {withdrawalAmounts.length > 1 && (
                        <button
                          type="button"
                          onClick={() => deleteWithdrawalAmount(index)}
                          className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                          title="Delete this option"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addWithdrawalAmount}
                    className="flex items-center space-x-2 px-3 py-2 text-sm text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    <span>Add Option</span>
                  </button>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {withdrawalAmounts.map((amount, index) => (
                    <span
                      key={index}
                      className="px-3 py-2 bg-gray-100 rounded-md text-sm font-medium"
                    >
                      ${amount}
                    </span>
                  ))}
                </div>
              )}
              <p className="text-xs text-gray-500 mt-2">
                Default withdrawal amounts. Check Balance and Cancel are included automatically.
              </p>
            </div>

            {/* PIN Settings */}
            <div className="border-t pt-6">
              <div className="flex items-center space-x-3 mb-4">
                <input
                  id="requirePin"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  checked={requirePin}
                  onChange={e => setRequirePin(e.target.checked)}
                />
                <label htmlFor="requirePin" className="text-sm font-medium text-gray-700">
                  Require PIN for access
                </label>
              </div>

              {requirePin && (
                <div>
                  <label
                    className="block text-sm font-medium text-gray-700 mb-2"
                    htmlFor="defaultPin"
                  >
                    PIN
                  </label>
                  <input
                    id="defaultPin"
                    type="password"
                    className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter 4-digit PIN"
                    value={defaultPin}
                    onChange={e => setDefaultPin(e.target.value)}
                    maxLength={10}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This PIN will be required to access the ATM
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Create Button */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleSaveAtm}
            disabled={isSaving}
            className="px-8 py-3 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Creating ATM...' : 'Create ATM'}
          </button>
        </div>
      </div>
    </Layout>
  );
};

export default CreateAtm;
